import { IOrgInfoNotificationTypePrefix } from "@clovis/server/src/app/domain/notification/create-notification/org/info/types";
import { IOrgNotificationTypePrefix } from "@clovis/server/src/app/domain/notification/create-notification/org/types";
import { IChatNotificationTypePrefix } from "@clovis/server/src/app/domain/notification/create-notification/project/chat/types";
import { IDocumentNotificationTypePrefix } from "@clovis/server/src/app/domain/notification/create-notification/project/document/types";
import { IProjectMiscNotificationTypePrefix } from "@clovis/server/src/app/domain/notification/create-notification/project/misc/types";
import { ITaskNotificationTypePrefix } from "@clovis/server/src/app/domain/notification/create-notification/project/task/types";
import * as React from "react";
import { z } from "zod";
import { useIsOffline } from "~/config/offline-storage/useIsOffline.hook";
import { SentryReact } from "~/config/sentry";
import type { UserNotifications } from "~/config/user/user-notifications/utils/types";
import { useBreakpoint } from "~/design-system/hooks";
import { Skeleton, SkeletonType } from "~/design-system/Skeleton";
import type { User_Notifications_Bool_Exp } from "~/gql/graphql";
import { ErrorPage } from "~/screens/App/components/Error/Error";
import { ErrorFallback } from "~/screens/App/components/ErrorFallback/ErrorFallback";
import type { ScreenProps } from "~/screens/App/components/Layout";
import { Screen, ScrollableContent } from "~/screens/App/components/Layout";
import { withQuery } from "~/screens/App/components/Query/Query";
import { UncachedDataOfflinePlaceholder } from "~/screens/App/components/UncachedDataOfflinePlaceholder/UncachedDataOfflinePlaceholder";
import { createComponentHook } from "~/types";
import { NotificationsContent } from "./components/NotificationsContent/NotificationsContent";
import { NotificationsSelectedItems } from "./components/NotificatonsSelectedItems/NotificationsSelectedItems";
import { FilterNotificationsButton } from "./components/UserNotificationsScreenActions/FilterNotificationsButton";
import { MarkAllAsReadButton } from "./components/UserNotificationsScreenActions/MarkAllAsReadButton";
import type { UserNotificationsScreen_NotificationsQuery } from "./UserNotificationsScreen.graphql";
import { useUserNotificationsScreen_NotificationsQuery } from "./UserNotificationsScreen.graphql";

type Filters = {
  types: {
    read: boolean;
    strong: boolean;
    weak: boolean;
  };
  categories: {
    task: boolean;
    org: boolean;
    documents: boolean;
    chat: boolean;
    project: boolean;
  };
  projectIds: string[];
};

const payloadPartialSchema = z.object({
  project: z
    .object({
      id: z.string().uuid(),
    })
    .optional(),
});

const ITEMS_PER_PAGE = 50;

const useUserNotificationsScreen = createComponentHook(() => {
  const { breakpoint } = useBreakpoint();

  const [offset, setOffset] = React.useState(ITEMS_PER_PAGE);

  const fetchMore = () => {
    setOffset((prev) => prev + ITEMS_PER_PAGE);
  };

  const defaultFilters = {
    categories: {
      chat: false,
      documents: false,
      org: false,
      project: false,
      task: false,
    },
    projectIds: [],
    types: {
      read: false,
      strong: true,
      weak: false,
    },
  };

  /* create filters to query */
  const [filters, setFilters] = React.useState<Filters>(defaultFilters);

  /* create where conditions (for types and categories) for query from filters values */
  const typesConditions: User_Notifications_Bool_Exp[] = [
    { read_at: { _is_null: !filters.types.read } },
  ];

  const { strong, weak } = filters.types;
  if (strong && strong != weak) {
    typesConditions.push({ is_strong: { _eq: true } });
  } else if (weak && strong != weak) {
    typesConditions.push({ is_strong: { _eq: false } });
  }

  if (!strong && !weak) {
    typesConditions.push({
      is_strong: { _neq: false },
    });

    typesConditions.push({
      is_strong: { _neq: true },
    });
  }

  const categoriesConditions = Object.entries(filters.categories)
    .filter(([, value]) => value)
    .map(([k]) => {
      switch (k) {
        case "org":
          return { type: { _like: `${IOrgNotificationTypePrefix}%` } };
        case "task":
          return { type: { _like: `${ITaskNotificationTypePrefix}%` } };
        case "documents":
          return { type: { _like: `${IDocumentNotificationTypePrefix}%` } };
        case "chat":
          return { type: { _like: `${IChatNotificationTypePrefix}%` } };
        case "project":
          return {
            _or: [
              { type: { _like: `${IProjectMiscNotificationTypePrefix}%` } },
              { type: { _like: `${IOrgInfoNotificationTypePrefix}%` } },
            ],
          };
      }
    });

  /* add types and categories conditions to the where only if length > 0 */
  const andConditions: User_Notifications_Bool_Exp[] = [];
  if (typesConditions.length) {
    andConditions.push({
      _and: typesConditions,
    });
  }
  if (categoriesConditions.length) {
    andConditions.push({
      _or: categoriesConditions as User_Notifications_Bool_Exp[],
    });
  }
  const formatedConditions =
    andConditions.length > 1
      ? { _and: andConditions }
      : andConditions.length > 0
      ? andConditions[0]
      : {};

  const [response] = useUserNotificationsScreen_NotificationsQuery({
    variables: {
      limit: offset,
      where: formatedConditions,
    },
  });

  const filteredNotificationsByProjects: UserNotificationsScreen_NotificationsQuery =
    {
      user_notifications:
        response.data?.user_notifications.filter(({ payload }) => {
          if (filters.projectIds.length === 0) return true;
          const parsedPayload = payloadPartialSchema.parse(payload);
          if (parsedPayload?.project) {
            const { id } = parsedPayload.project;
            return filters.projectIds.includes(id);
          }
        }) ?? [],
    };

  const screenActions =
    breakpoint === "mobile"
      ? {
          actions: (
            <FilterNotificationsButton
              setFilters={setFilters}
              filters={filters}
            />
          ),
          extraActions: <MarkAllAsReadButton />,
        }
      : {
          primaryAction: <MarkAllAsReadButton />,
        };

  return {
    actions: {
      fetchMore,
      setFilters,
    },
    state: {
      breakpoint,
      defaultFilters,
      filters,
      notifications: filteredNotificationsByProjects?.user_notifications as
        | UserNotifications
        | undefined,
      response,
      screenActions,
    },
  };
});

function UserNotificationBaseScreen(props: ScreenProps) {
  return (
    <SentryReact.ErrorBoundary
      fallback={ErrorFallback}
      beforeCapture={(scope) => {
        scope.setTag("ScreenError", "UserNotificationScreenError");
      }}
    >
      <Screen title="User Notifications" {...props}>
        <ScrollableContent fullHeight overflowVisible>
          {props.children}
        </ScrollableContent>
      </Screen>
    </SentryReact.ErrorBoundary>
  );
}
function UserNotificationsScreen() {
  const { actions, state } = useUserNotificationsScreen();
  const isOffline = useIsOffline();

  if (isOffline && !state.response.data) {
    return <UncachedDataOfflinePlaceholder />;
  }

  return withQuery({
    response: state.response,
  })({
    Error: (
      <UserNotificationBaseScreen {...state.screenActions}>
        <ErrorPage />
      </UserNotificationBaseScreen>
    ),
    Loading: <Skeleton type={SkeletonType.FILES_LIST} />,
    Success: function () {
      return (
        <UserNotificationBaseScreen {...state.screenActions}>
          <>
            <NotificationsContent
              notifications={state.notifications}
              setFilters={actions.setFilters}
              defaultFilters={state.defaultFilters}
              filters={state.filters}
              fetchMore={actions.fetchMore}
            />
            <NotificationsSelectedItems />
          </>
        </UserNotificationBaseScreen>
      );
    },
  });
}

export { UserNotificationsScreen };
export type { Filters };
